import { useState, useEffect, useRef } from"react";
import { useTranslation } from"react-i18next";
import axios from"axios";
import Layout from"@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from"@/components/ui/card";
import { Button } from"@/components/ui/button";
import { Badge } from"@/components/ui/badge";
import { Input } from"@/components/ui/input";
import { Label } from"@/components/ui/label";
import { Textarea } from"@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from"@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from"@/components/ui/select";
import { FileText, Plus, Clock, CheckCircle, XCircle, Upload, Image, X, Paperclip } from"lucide-react";
import { toast } from"sonner";

const ContractorReportsPage = ({ user, onLogout }) => {
 const { t } = useTranslation();
 const [dashboard, setDashboard] = useState(null);
 const [reports, setReports] = useState([]);
 const [loading, setLoading] = useState(true);
 const [dialogOpen, setDialogOpen] = useState(false);
 const [submitting, setSubmitting] = useState(false);
 const [uploadingFiles, setUploadingFiles] = useState(false);
 const [attachments, setAttachments] = useState([]);
 const fileInputRef = useRef(null);
 const [formData, setFormData] = useState({
 task_id:"",
 title:"",
 description:"",
 progress_percentage: 0
 });

 useEffect(() => {
 loadData();
 }, []);

 const loadData = async () => {
 try {
 const [dashboardRes, reportsRes] = await Promise.all([
 axios.get("/dashboard/contractor"),
 axios.get("/contractor/progress-reports")
 ]);
 setDashboard(dashboardRes.data);
 setReports(reportsRes.data);
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 } finally {
 setLoading(false);
 }
 };

 const handleFileSelect = async (e) => {
 const files = Array.from(e.target.files || []);
 if (files.length === 0) return;
 
 // Check file size (25MB limit)
 const MAX_SIZE = 25 * 1024 * 1024;
 const validFiles = files.filter(file => {
 if (file.size > MAX_SIZE) {
 toast.error(`${file.name} is too large. Max size is 25MB.`);
 return false;
 }
 return true;
 });
 
 if (validFiles.length === 0) return;
 
 setUploadingFiles(true);
 
 try {
 const uploadPromises = validFiles.map(async (file) => {
 const formData = new FormData();
 formData.append("file", file);
 
 const response = await axios.post("/upload", formData, {
 headers: {"Content-Type":"multipart/form-data"}
 });
 
 return {
 url: response.data.url,
 filename: response.data.filename || file.name,
 type: file.type
 };
 });
 
 const uploadedFiles = await Promise.all(uploadPromises);
 setAttachments(prev => [...prev, ...uploadedFiles]);
 toast.success(`${uploadedFiles.length} file(s) uploaded successfully`);
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 } finally {
 setUploadingFiles(false);
 if (fileInputRef.current) {
 fileInputRef.current.value ="";
 }
 }
 };

 const removeAttachment = (index) => {
 setAttachments(prev => prev.filter((_, i) => i !== index));
 };

 const handleSubmit = async (e) => {
 e.preventDefault();
 if (!formData.title || !formData.description) {
 toast.error(t("validation.required"));
 return;
 }

 setSubmitting(true);
 try {
 const assignment = dashboard?.assignments?.[0];
 await axios.post("/contractor/progress-reports", {
 project_id: assignment?.project_id,
 task_id: formData.task_id || null,
 title: formData.title,
 description: formData.description,
 progress_percentage: parseInt(formData.progress_percentage) || 0,
 attachments: attachments.map(a => a.url)
 });
 toast.success(t("messages.saveSuccess"));
 setDialogOpen(false);
 setFormData({ task_id:"", title:"", description:"", progress_percentage: 0 });
 setAttachments([]);
 loadData();
 } catch (error) {
 toast.error(error.response?.data?.detail || t("messages.saveFailed"));
 } finally {
 setSubmitting(false);
 }
 };

 const getStatusIcon = (status) => {
 switch (status) {
 case"approved":
 return <CheckCircle className="w-5 h-5 text-green-500"/>;
 case"rejected":
 return <XCircle className="w-5 h-5 text-red-500"/>;
 default:
 return <Clock className="w-5 h-5 text-yellow-500"/>;
 }
 };

 const getStatusColor = (status) => {
 switch (status) {
 case"approved":
 return"bg-emerald-500/10 text-emerald-700 border-green-200/60";
 case"rejected":
 return"bg-rose-500/10 text-rose-700 border-red-200/60";
 default:
 return"bg-amber-500/10 text-amber-700 border-yellow-200/60";
 }
 };

 const isImageUrl = (url) => {
 return /\.(jpg|jpeg|png|webp|gif)$/i.test(url);
 };

 if (loading) {
 return (
 <Layout user={user} onLogout={onLogout}>
 <div className="flex items-center justify-center h-64">
 <div className="text-slate-600">Loading...</div>
 </div>
 </Layout>
 );
 }

 return (
 <Layout user={user} onLogout={onLogout}>
 <div className="space-y-6">
 <div className="flex items-center justify-between">
 <div>
 <h1 className="font-display text-3xl font-bold tracking-tight uppercase text-slate-900 mb-1">
 Engineer Reports
 </h1>
 <p className="text-slate-600">
 Submit progress reports for engineer review and approval
 </p>
 </div>
 <Button onClick={() => setDialogOpen(true)} className="bg-slate-900 hover:bg-slate-800:bg-zinc-200">
 <Plus className="w-4 h-4 mr-2"/>
 New Report
 </Button>
 </div>

 {/* Info Card */}
 <Card className="bg-blue-50/60">
 <CardContent className="p-4">
 <div className="flex items-center gap-3">
 <Upload className="w-8 h-8 text-blue-600"/>
 <div>
 <h4 className="font-semibold text-slate-900">Progress Reports</h4>
 <p className="text-sm text-slate-600">
 Submit reports with photos and documents to keep the engineer updated on your progress. Reports will be reviewed and approved by the project engineer.
 </p>
 </div>
 </div>
 </CardContent>
 </Card>

 {/* Reports List */}
 {reports.length > 0 ? (
 <div className="space-y-4">
 {reports.map((report) => (
 <Card key={report.id} className="">
 <CardContent className="p-4">
 <div className="flex items-start justify-between">
 <div className="flex items-start gap-4 flex-1">
 {getStatusIcon(report.status)}
 <div className="flex-1">
 <h3 className="font-semibold text-slate-900">{report.title}</h3>
 <p className="text-sm text-slate-600 mt-1">{report.description}</p>
 <div className="flex flex-wrap items-center gap-3 mt-2 text-[11px] text-slate-500">
 <span>Progress: {report.progress_percentage}%</span>
 <span>Submitted: {new Date(report.created_at).toLocaleDateString()}</span>
 {report.reviewed_at && (
 <span className={`font-medium ${report.status === "approved" ? "text-emerald-600" : "text-red-600"}`}>
 {report.status === "approved" ? "✓ Approved" : "✗ Rejected"} by {report.reviewer_name || "Engineer"} · {new Date(report.reviewed_at).toLocaleDateString()}
 </span>
 )}
 </div>
 
 {/* Material Quantities */}
 {report.material_total > 0 && (
 <div className="mt-2 inline-flex items-center gap-1.5 bg-blue-50 border border-blue-200 rounded px-2 py-0.5">
 <span className="text-xs font-semibold text-blue-700">
 {report.material_total} {report.material_unit_of_measure || "m\u00B2"}
 </span>
 {report.engineer_adjusted_quantities && (
 <span className="text-[10px] text-amber-600">(adjusted by engineer)</span>
 )}
 </div>
 )}

 {/* Attachments */}
 {report.attachments && report.attachments.length > 0 && (
 <div className="mt-3">
 <p className="text-[11px] font-medium text-slate-500 mb-2">Attachments:</p>
 <div className="flex flex-wrap gap-2">
 {report.attachments.map((url, idx) => (
 <a
 key={idx}
 href={url}
 target="_blank"
 rel="noopener noreferrer"
 className="flex items-center gap-1 px-2 py-1 bg-white/60 rounded text-xs text-blue-600 hover:bg-slate-200"
 >
 {isImageUrl(url) ? <Image className="w-3 h-3"/> : <Paperclip className="w-3 h-3"/>}
 File {idx + 1}
 </a>
 ))}
 </div>
 </div>
 )}
 
 {report.review_notes && (
 <div className="mt-2 p-2 bg-white/60 rounded text-sm">
 <span className="font-medium">Engineer Notes:</span> {report.review_notes}
 </div>
 )}
 </div>
 </div>
 <Badge variant="outline"className={getStatusColor(report.status)}>
 {report.status}
 </Badge>
 </div>
 </CardContent>
 </Card>
 ))}
 </div>
 ) : (
 <Card className="">
 <CardContent className="text-center py-12">
 <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3"/>
 <p className="text-slate-600 mb-4">No progress reports submitted yet</p>
 <Button onClick={() => setDialogOpen(true)}>
 <Plus className="w-4 h-4 mr-2"/>
 Submit Your First Report
 </Button>
 </CardContent>
 </Card>
 )}

 {/* New Report Dialog */}
 <Dialog open={dialogOpen} onOpenChange={(open) => {
 setDialogOpen(open);
 if (!open) {
 setAttachments([]);
 setFormData({ task_id:"", title:"", description:"", progress_percentage: 0 });
 }
 }}>
 <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
 <DialogHeader>
 <DialogTitle>Submit Progress Report</DialogTitle>
 </DialogHeader>
 <form onSubmit={handleSubmit} className="space-y-4">
 {/* Task Selection */}
 {dashboard?.my_tasks && dashboard.my_tasks.length > 0 && (
 <div>
 <Label htmlFor="task">Related Task (Optional)</Label>
 <Select 
 value={formData.task_id} 
 onValueChange={(val) => setFormData(prev => ({ ...prev, task_id: val }))}
 >
 <SelectTrigger>
 <SelectValue placeholder="Select a task"/>
 </SelectTrigger>
 <SelectContent>
 {dashboard.my_tasks.map((task) => (
 <SelectItem key={task.id} value={task.id}>
 {task.job_name} (Phase {task.phase})
 </SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>
 )}

 {/* Title */}
 <div>
 <Label htmlFor="title">Report Title *</Label>
 <Input
 value={formData.title}
 onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
 placeholder="e.g., Weekly Progress Update"
 />
 </div>

 {/* Progress Percentage */}
 <div>
 <Label htmlFor="progress">Progress Percentage</Label>
 <div className="flex items-center gap-2">
 <Input
 type="number"
 min="0"
 max="100"
 value={formData.progress_percentage}
 onChange={(e) => setFormData(prev => ({ ...prev, progress_percentage: e.target.value }))}
 className="w-24"
 />
 <span className="text-slate-600">%</span>
 </div>
 </div>

 {/* Description */}
 <div>
 <Label htmlFor="description">Description *</Label>
 <Textarea
 value={formData.description}
 onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
 placeholder="Describe the work completed, challenges faced, and next steps..."
 rows={4}
 />
 </div>

 {/* File Upload */}
 <div>
 <Label>Attachments (Photos/Documents)</Label>
 <div className="mt-2">
 <input
 ref={fileInputRef}
 type="file"
 accept="image/*,application/pdf"
 multiple
 onChange={handleFileSelect}
 className="hidden"
 id="file-upload"
 />
 <label
 htmlFor="file-upload"
 className={`flex items-center justify-center gap-2 w-full p-4 border-2 border-dashed rounded-sm cursor-pointer transition-colors ${
 uploadingFiles 
 ?"border-slate-200 bg-white/50 cursor-wait"
 :"border-slate-200 hover:border-blue-400 hover:bg-blue-50/80"
 }`}
 >
 {uploadingFiles ? (
 <>
 <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"/>
 <span className="text-slate-600">Uploading...</span>
 </>
 ) : (
 <>
 <Upload className="w-5 h-5 text-indigo-600"/>
 <span className="text-slate-600">Click to upload photos or documents</span>
 </>
 )}
 </label>
 <p className="text-[11px] text-slate-500 mt-1">Max 25MB per file. Supports JPG, PNG, WebP, PDF.</p>
 </div>

 {/* Uploaded Files List */}
 {attachments.length > 0 && (
 <div className="mt-3 space-y-2">
 {attachments.map((file, index) => (
 <div key={index} className="flex items-center justify-between p-2 bg-white/50 rounded-sm">
 <div className="flex items-center gap-2">
 {file.type?.startsWith("image/") ? (
 <Image className="w-4 h-4 text-blue-600"/>
 ) : (
 <FileText className="w-4 h-4 text-red-500"/>
 )}
 <span className="text-sm text-[#334155] truncate max-w-[200px]">{file.filename}</span>
 </div>
 <Button
 type="button"
 variant="ghost"
 size="sm"
 onClick={() => removeAttachment(index)}
 className="h-6 w-6 p-0"
 >
 <X className="w-4 h-4 text-slate-500 hover:text-red-500"/>
 </Button>
 </div>
 ))}
 </div>
 )}
 </div>

 <div className="flex gap-3 justify-end">
 <Button type="button"variant="outline"onClick={() => setDialogOpen(false)}>
 Cancel
 </Button>
 <Button type="submit"disabled={submitting || uploadingFiles} className="bg-slate-900 hover:bg-slate-800:bg-zinc-200">
 {submitting ?"Submitting...":"Submit Report"}
 </Button>
 </div>
 </form>
 </DialogContent>
 </Dialog>
 </div>
 </Layout>
 );
};

export default ContractorReportsPage;
